import React, { useState, useEffect } from 'react'
import type { PresentationIndustry, QuoteDocument, QuoteFile, PresentationLibrary } from 'paintscout'
import { getFeature } from '@paintscout/util/builder'
import { isFilePdf, isFileImage } from '@paintscout/util/s3'
import {
  Button,
  PopupMenuItem,
  makeStyles,
  ClickAwayListener,
  PresentationFilePreview as FilePreview,
  ImageSmallIcon,
  DEFAULT_FILE_ACCEPT
} from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses, PresentationFilePreviewProps as FilePreviewProps } from '@paintscout/ui'
import { Placeholder, PresEditorTooltip, ViewMediaDialog } from '../../components'
import SyncIcon from '@material-ui/icons/Sync'
import { PresentationLibraryDialog } from '../../dialogs'
import isEqual from 'lodash/isEqual'
import classnames from 'classnames'
import { useClientOptions, useDialogs, PresentationEmbedMedia as EmbedMedia } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useQuote, usePresentation } from '../../../context'
import { clearDrillInOnClickAway } from '../../util/presentationEditorDom'
import { applyMultiFileSelection } from '../../util/multiFileSelection'
import { addNewFilesToLibrary } from '../../util/addNewFilesToLibrary'

export interface SmartSourceOption {
  key: string
  label: string
  getFile: (quote: QuoteDocument) => QuoteFile | null
}

interface InlineEditableMediaProps {
  accept?: string
  presentationLibrary?: {
    addFilesToLibrary: (args: { files: QuoteFile[] }) => void
    library: PresentationLibrary
    saveLibrary: (args: { library: PresentationLibrary }) => void
  }
  classes?: StyleClasses<typeof useStyles>
  file: QuoteFile
  getFocusFromSection?: boolean
  handleEdit: (content: QuoteFile) => void
  hasRightSideMenus?: boolean
  isAnimated?: boolean
  isEditable?: boolean
  isSettings?: boolean
  mediaOptionsProps?: {
    blockPrefix: string
    fieldSuffix: string
    hideAspectOptions?: boolean
  }
  placeholderButtonText?: string
  presentationIndustry: PresentationIndustry
  showAllPages?: boolean
  square?: boolean
  youtube?: boolean
  filePreviewProps?: Partial<FilePreviewProps>
  /** Available "smart content" sources (e.g. "first estimate image") this field can bind to. */
  smartSourceOptions?: SmartSourceOption[]
  /** Currently-selected smart source key (null/undefined = manual). */
  smartSourceKey?: string | null
  /** Persists the smart source selection (a sibling field, alongside the manual file value). */
  onSmartSourceChange?: (key: string | null) => void
  /**
   * Accepts the files past the first when this field's picker returns several at once (#23709).
   * Only a Gallery tile passes it, and passing it is also what puts the Library dialog into
   * multi-select mode for this field — every other field stays single-pick.
   */
  onAddExtraFiles?: (files: QuoteFile[]) => void
}

const useStyles = makeStyles<Theme, InlineEditableMediaProps>(
  (theme: Theme) => {
    return {
      root: (props) => ({
        display: 'flex',
        justifyContent: props.file?.presentationOptions?.horizontalAlign || 'center',
        minHeight: 50
      }),
      size: {
        position: 'relative',
        width: '100%',
        height: '100%'
      },
      hovered: {
        cursor: 'pointer',
        '&:hover': {
          outline: `2px solid ${theme.palette.presentation.sectionBorder.hover}`,
          zIndex: 1,
          '& $iconMenuRoot': {
            display: 'flex'
          }
        }
      },
      focused: {
        outline: `2px solid ${theme.palette.presentation.sectionBorder.focus}`,
        zIndex: 1
      },
      image: (
        props,
        clipPath = props.file?.presentationOptions?.clipPath || 'none',
        clipPathOrientation = props.file?.presentationOptions?.clipPathOrientation || 'top'
      ) => ({
        '&$image': {
          width: '100%',
          aspectRatio: getAspectRatio({ file: props.file, maxSquare: false }),
          objectPosition: getObjectPosition(props.file),
          clipPath: getClipPath(clipPath, clipPathOrientation),
          objectFit:
            props.file?.presentationOptions?.imageFit ??
            (props.file?.presentationOptions?.aspectRatio !== 'unset' ? 'cover' : 'contain'),
          [theme.breakpoints.down('sm')]: {
            aspectRatio: getAspectRatio({ file: props.file, maxSquare: true })
          },
          borderRadius: 0
        }
      }),
      youtubeWrapper: {
        pointerEvents: 'auto'
      },
      link: {
        display: 'block'
      },
      mediaColumn: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      },
      description: {
        display: 'block',
        marginTop: theme.spacing(1),
        fontSize: '0.875rem',
        color: theme.palette.text.secondary
      },
      viewDocumentButton: {
        marginTop: theme.spacing(1)
      },
      dropzone: (props) => ({
        height: props.file ? 'auto' : 150
      }),
      editable: {
        cursor: 'pointer',
        height: 'inherit'
      },
      mediaPlaceholder: {
        aspectRatio: '1/1'
      },
      iconMenuRoot: {
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.borderRadius.md,
        bottom: theme.spacing(2),
        boxShadow: theme.boxShadow[1],
        display: 'none',
        left: theme.spacing(2),
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        zIndex: 3
      },
      iconMenuRightSide: {
        right: theme.spacing(2),
        left: 'auto'
      },
      iconMenuRootFocused: {
        display: 'flex'
      }
    }
  },
  { name: 'InlineEditableMedia' }
)

export default function InlineEditableMedia(props: InlineEditableMediaProps) {
  const classes = useStyles(props)
  const {
    accept,
    presentationLibrary,
    getFocusFromSection,
    handleEdit,
    hasRightSideMenus,
    isAnimated,
    isEditable,
    isSettings,
    mediaOptionsProps,
    placeholderButtonText,
    presentationIndustry,
    showAllPages,
    square,
    youtube: hasYoutube = true,
    filePreviewProps,
    smartSourceOptions,
    smartSourceKey,
    onSmartSourceChange,
    onAddExtraFiles
  } = props

  const { blockPrefix, fieldSuffix } = mediaOptionsProps || {}
  const { options, clientId } = useClientOptions()
  const s3UploadFeatureEnabled = getFeature({ options, path: 's3ImageUpload.enabled' })
  const { s3MediaUpload: s3MediaUploadEnabled } = useFlags()
  const { quote } = useQuote()

  const [file, setFile] = useState(props.file)
  // isFocused mirrors the parent block's own focus (isEditable) — every field in a focused block is
  // immediately interactive in one click; a *second* click on this specific image is what drills into
  // its own options (see the onClick below). Gates the YouTube URL input (EmbedMedia's `disabled`
  // prop) and the Logo/Banner-poster getFocusFromSection auto-open for an *empty* slot. The blue
  // "focused" outline itself tracks isDrilledIn instead, so only the image actually clicked shows it —
  // not every image in the block.
  const isFocused = isEditable
  const { focusedElement, setFocusedElement } = usePresentation()

  // this field's slot in the inspector's drill-in state (#22201) — the formik path is unique per
  // rendered field, since blockPrefix carries the page/section/block indices
  const optionsKey = `media::${blockPrefix}.${fieldSuffix}`
  const isDrilledIn = focusedElement?.type === 'media' && focusedElement.key === optionsKey
  const format = file?.format
  const { openDialog, dismissDialog, stack: dialogStack } = useDialogs()

  // A smart source binds this field to a live, quote-derived image (e.g. "first estimate
  // image") instead of a fixed one — resolved fresh from whichever quote is in context (the
  // editor's preview quote, or the actual customer's quote in customer-view), so the same
  // template shows the right image per quote. The manually-picked `file` is preserved
  // independently underneath so switching the smart source off reverts to it.
  const activeSmartSource = smartSourceOptions?.find((option) => option.key === smartSourceKey)
  const resolvedFile = activeSmartSource ? activeSmartSource.getFile(quote) ?? file : file
  // isFilePdf (not the strict `format === 'pdf'`) — a file's `format` is stamped once at upload
  // from the browser-reported MIME type and never backfilled, so some genuine PDFs never get
  // `format === 'pdf'`. Falling back to `type`/`src` here is what makes the button (and the
  // click-to-preview below) show consistently across presentations regardless of upload vintage.
  const isPdf = isFilePdf(resolvedFile)
  // Documents/Services rows are marked at conversion time (converters/shared.ts, ENG-1444) so the
  // "View Document" button can also show for a non-PDF document (e.g. an image) without showing
  // on every ordinary image block (Gallery, Banner, Logo, etc.) that reuses this component.
  const isDocumentImage = !!resolvedFile?.presentationOptions?.isDocumentImage && isFileImage(resolvedFile)
  const canViewFile = isPdf || isDocumentImage

  const handleToggleSmartSource = (key: string) => {
    onSmartSourceChange?.(smartSourceKey === key ? null : key)
  }

  const handleViewFile = (_page) => {
    openDialog(ViewMediaDialog, {
      files: [resolvedFile],
      enableDownload: !isSettings,
      onClose: dismissDialog
    })
  }

  const handleMediaLibraryDialog = () => {
    openDialog(PresentationLibraryDialog, {
      handleSelect: handleLibrarySelect,
      handleSelectMultiple: onAddExtraFiles ? handleLibrarySelectMultiple : undefined,
      presentationLibrary,
      presentationIndustry,
      specificQuery: file?.pexelsQuery,
      onCancel: () => dismissDialog(),
      allowYoutube: hasYoutube
    })
  }

  // update file in case of prop.file change (e.g., image to image swap from ThreeColumnOptions)
  // note: this is also triggered by handleEdit() in this component
  useEffect(() => {
    setFile(props.file)
  }, [props.file])

  useEffect(() => {
    if (!isEditable) {
      // remove focus if parent block loses focus
      // instead of listening for a blur on the editor, we listen for a click outside of the block
      if (!isEqual(props.file, file)) {
        handleEdit(file)
      }
      if (file?.format === 'youtube' && !file.youtubeId) {
        handleChangeFormat('upload')
      }
      if (isDrilledIn) {
        setFocusedElement(null)
      }
    }
  }, [isEditable])

  const applyFileChange = (newFile: QuoteFile) => {
    const comboFile = file
      ? {
          ...newFile,
          presentationOptions: file.presentationOptions,
          youtubeId: newFile.youtubeId ?? file.youtubeId
        }
      : newFile
    setFile(comboFile)
    handleEdit(comboFile)
    return comboFile
  }

  // Direct changes (drag/drop upload, youtube URL entry) never touch the library
  // themselves, so auto-add it here if it's new.
  const handleChange = (newFile: QuoteFile) => {
    const comboFile = applyFileChange(newFile)

    addNewFilesToLibrary({ files: [comboFile], presentationLibrary })
  }

  // Several files dropped on this slot at once (#23709). The first fills the slot, exactly as a
  // single drop would; the rest go to the block that owns the slot. None of them came through the
  // Library dialog, so they are auto-added to the library here, same rule as handleChange.
  const handleChangeMultiple = (newFiles: QuoteFile[]) => {
    const allFiles = applyMultiFileSelection({
      files: newFiles,
      applyFirst: applyFileChange,
      onRest: onAddExtraFiles
    })

    addNewFilesToLibrary({ files: allFiles, presentationLibrary })
  }

  // PresentationLibraryDialog already adds new uploads/Pexels picks to the library and
  // saves them itself (so it can show a single loading state and close on success) —
  // applying the file change here without also auto-adding avoids a second, racing save
  // to the same library.
  const handleLibrarySelect = (newFile: QuoteFile) => {
    applyFileChange(newFile)
  }

  // Multi-select pick (#23709): the first file fills this slot, exactly as a single pick would, and
  // the rest go to the block that owns the slot — this component only ever holds one file. The
  // dialog has already added them all to the library and saved it, same as the single-pick path.
  const handleLibrarySelectMultiple = (newFiles: QuoteFile[]) => {
    applyMultiFileSelection({ files: newFiles, applyFirst: applyFileChange, onRest: onAddExtraFiles })
  }

  const handleChangeFormat = (format: QuoteFile['format']) => {
    setFile({ ...file, format })
    handleEdit({ ...file, format }) // this is necessary for video banner blocks to update their block options (not all layouts are available for video) without having to blur the block
  }

  if (!isSettings) {
    const { link, description } = resolvedFile?.presentationOptions ?? {}
    const preview = resolvedFile && (
      <FilePreview
        classes={{
          image: classes.image,
          youtubeWrapper: classes.youtubeWrapper,
          size: classes.size
        }}
        onPageSelect={isPdf && !showAllPages ? handleViewFile : null}
        showAllPages={showAllPages}
        square={square}
        className={classes.root}
        file={resolvedFile as any}
        isAnimated={isAnimated}
        inPresentation
        inGallery
        {...filePreviewProps}
      />
    )

    if (!resolvedFile) {
      return null
    }

    return (
      <div className={classes.mediaColumn}>
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" className={classes.link}>
            {preview}
          </a>
        ) : (
          preview
        )}
        {description && <span className={classes.description}>{description}</span>}
        {canViewFile && !showAllPages && (
          <Button
            className={classes.viewDocumentButton}
            onClick={() => handleViewFile(0)}
            aria-label={resolvedFile.title ? `View document: ${resolvedFile.title}` : 'View document'}
          >
            View Document
          </Button>
        )}
      </div>
    )
  } else
    return (
      <ClickAwayListener
        onClickAway={(ev) => {
          // Only the drill-in needs clearing here — deselecting the block itself on click-away is the
          // parent block's own ClickAwayListener's job (Presentation.tsx), which already runs for any
          // click outside the block, isFocused/isEditable included. `skip: getFocusFromSection` avoids
          // dropping focus from an element that just auto-selected itself.
          clearDrillInOnClickAway({
            target: ev.target,
            isDrilledIn,
            setFocusedElement,
            skip: getFocusFromSection
          })
        }}
        {...(dialogStack.length && { mouseEvent: false, touchEvent: false })}
      >
        <div
          className={classnames({
            [classes.size]: true,
            [classes.hovered]: !isDrilledIn && !!(resolvedFile || smartSourceOptions?.length),
            [classes.focused]: isDrilledIn
          })}
          onClick={() => {
            if (!resolvedFile) {
              handleMediaLibraryDialog()
            }
          }}
        >
          {!!smartSourceOptions?.length && (
            <div
              className={classnames(classes.iconMenuRoot, {
                [classes.iconMenuRootFocused]: isFocused,
                [classes.iconMenuRightSide]: hasRightSideMenus
              })}
            >
              {smartSourceOptions?.map((option) => (
                <PresEditorTooltip key={option.key} title={option.label}>
                  <PopupMenuItem
                    icon={SyncIcon}
                    iconOnly
                    selected={smartSourceKey === option.key}
                    onClick={(ev) => {
                      ev.stopPropagation()
                      handleToggleSmartSource(option.key)
                    }}
                  />
                </PresEditorTooltip>
              ))}
            </div>
          )}
          {/* Once a file exists — image, video, or youtube — replacing it always goes through the
              Library dialog, so there's no need for the interactive EmbedMedia/Dropzone here —
              a plain FilePreview displays it (the YouTube URL itself is edited from the
              inspector's media options once drilled in, not inline). */}
          {resolvedFile && (
            <div className={classes.mediaColumn}>
              <div
                className={classes.root}
                onClick={(ev) => {
                  // Block not focused yet: let the click bubble so it focuses the parent block first,
                  // same as clicking any other part of the block.
                  if (!isEditable) {
                    return
                  }

                  // Block already focused: a single click on this image drills straight into its own
                  // options; a click after *that* (already drilled in) swaps it via the Library dialog.
                  ev.stopPropagation()

                  if (!isDrilledIn) {
                    setFocusedElement({ type: 'media', key: optionsKey, allowYoutube: hasYoutube })
                    return
                  }

                  handleMediaLibraryDialog()
                }}
              >
                <FilePreview
                  classes={{
                    image: classes.image,
                    youtubeWrapper: classes.youtubeWrapper,
                    size: classes.size
                  }}
                  showAllPages={showAllPages}
                  square={square}
                  file={resolvedFile as any}
                  isAnimated={isAnimated}
                  inPresentation
                  inGallery
                  // only once drilled in, since that's when a click swaps rather than selecting — it's
                  // the hint for the next click of that flow
                  showSwapOnHover={isDrilledIn}
                  {...filePreviewProps}
                />
              </div>
              {resolvedFile.presentationOptions?.description && (
                <span className={classes.description}>{resolvedFile.presentationOptions.description}</span>
              )}
              {canViewFile && !showAllPages && (
                <Button
                  className={classes.viewDocumentButton}
                  onClick={(ev) => {
                    // Don't let this click drill into (or re-focus) the underlying media field.
                    ev.stopPropagation()
                    handleViewFile(0)
                  }}
                  aria-label={resolvedFile.title ? `View document: ${resolvedFile.title}` : 'View document'}
                >
                  View Document
                </Button>
              )}
            </div>
          )}
          {!resolvedFile && isFocused && (
            <div className={classes.root}>
              <EmbedMedia
                classes={classes}
                value={file ?? { visibility: 'visible', format: format }}
                noClearButton
                // Clicking an empty slot should only open the Library dialog (this component's own
                // onClick above) — not also pop the native OS file picker for the same click.
                noClick
                showSwapOnHover
                square={square}
                accept={accept ?? DEFAULT_FILE_ACCEPT}
                maxWidth={1280}
                maxHeight={1600}
                disabled={!isFocused}
                onChange={handleChange}
                onChangeMultiple={onAddExtraFiles ? handleChangeMultiple : undefined}
                youtube={false}
                format={format}
                showAllPages={showAllPages}
                filePreviewProps={filePreviewProps}
                companyId={clientId}
                s3UploadFeatureEnabled={s3UploadFeatureEnabled}
                s3MediaUploadEnabled={s3MediaUploadEnabled}
              />
            </div>
          )}
          {!resolvedFile && !isFocused && (
            <Placeholder
              classes={{ placeholder: classnames(classes.image, classes.mediaPlaceholder) }}
              buttonText={placeholderButtonText}
              Icon={ImageSmallIcon}
            />
          )}
        </div>
      </ClickAwayListener>
    )
}

// Returns the CSS `aspect-ratio` value for `file`, or the literal string `'unset'` when `file` is
// missing or has no ratio configured — callers must check for `'unset'` themselves rather than
// passing it straight through, since `aspect-ratio: unset` is valid CSS but rarely what's intended.
export function getAspectRatio(args: { file: QuoteFile | null | undefined; maxSquare: boolean }) {
  const { file, maxSquare } = args
  if (!file) return 'unset'
  const aspectRatio = (file.presentationOptions?.aspectRatio || 'unset').toString()
  const defaultImageOrientation = file.width < file.height ? 'portrait' : 'landscape'
  const imageOrientation = file.presentationOptions?.imageOrientation || defaultImageOrientation
  if (imageOrientation === 'landscape') return aspectRatio
  else {
    if (maxSquare) return '1 / 1'
    const slashIndex = aspectRatio.indexOf('/')
    if (slashIndex === -1) return aspectRatio
    else return aspectRatio.substring(slashIndex + 1) + '/' + aspectRatio.substring(0, slashIndex)
  }
}

export function getObjectPosition(file: QuoteFile | null | undefined) {
  if (!file) return '50% 50%'
  if (file.format === 'pdf') return '50% 0%'
  const objectPositionX = file.presentationOptions?.objectPositionX ?? 50
  const objectPositionY = file.presentationOptions?.objectPositionY ?? 50
  return `${objectPositionX}% ${objectPositionY}%`
}

function getClipPath(clipPath: string, clipPathOrientation: 'top' | 'bottom' | 'left' | 'right') {
  // switch
  switch (clipPath) {
    case 'circle':
      return 'circle()'
    case 'parallelogram':
      if (clipPathOrientation === 'top') return 'polygon(0 25%, 100% 0, 100% 75%, 0 100%)'
      else if (clipPathOrientation === 'right') return 'polygon(25% 0%, 100% 0%, 75% 100%, 0% 100%)'
      else if (clipPathOrientation === 'bottom') return 'polygon(0 0, 100% 25%, 100% 100%, 0 75%)'
      else if (clipPathOrientation === 'left') return 'polygon(0% 0%, 75% 0%, 100% 100%, 25% 100%)'
    // falls through
    case 'chevron':
      if (clipPathOrientation === 'top') return 'polygon(100% 20%, 100% 100%, 50% 80%, 0 100%, 0 20%, 50% 0)'
      else if (clipPathOrientation === 'right') return 'polygon(80% 0%, 100% 50%, 80% 100%, 0% 100%, 20% 50%, 0% 0%)'
      else if (clipPathOrientation === 'bottom') return 'polygon(100% 0, 100% 80%, 50% 100%, 0 80%, 0 0, 50% 20%)'
      else if (clipPathOrientation === 'left') return 'polygon(100% 0%, 80% 50%, 100% 100%, 20% 100%, 0% 50%, 20% 0%)'
    // falls through
    case 'scoop':
      if (clipPathOrientation === 'top') return 'ellipse(190% 100% at 100% 100%)'
      else if (clipPathOrientation === 'right') return 'ellipse(100% 190% at 0 100%)'
      else if (clipPathOrientation === 'bottom') return 'ellipse(190% 100% at 0 0)'
      else if (clipPathOrientation === 'left') return 'ellipse(100% 190% at 100% 0)'
    // falls through
    case 'arch':
      if (clipPathOrientation === 'top') return 'inset(0 round 9999px 9999px 0% 0%)'
      else if (clipPathOrientation === 'right') return 'inset(0 round 0 9999px 9999px 0)'
      else if (clipPathOrientation === 'bottom') return 'inset(0 round 0 0 9999px 9999px)'
      else if (clipPathOrientation === 'left') return 'inset(0 round 9999px 0 0 9999px)'
    // falls through
    case 'leaf':
      if (clipPathOrientation === 'right' || clipPathOrientation === 'top') return 'inset(0 round 9999px 0 9999px 0)'
      else if (clipPathOrientation === 'left' || clipPathOrientation === 'bottom')
        return 'inset(0 round 0 9999px 0 9999px)'
    // falls through
    case 'drop':
      if (clipPathOrientation === 'top') return 'inset(0 round 0 9999px 9999px 9999px)'
      else if (clipPathOrientation === 'right') return 'inset(0 round 9999px 0 9999px 9999px)'
      else if (clipPathOrientation === 'bottom') return 'inset(0 round 9999px 9999px 0 9999px)'
      else if (clipPathOrientation === 'left') return 'inset(0 round 9999px 9999px 9999px 0)'
    // falls through
    case 'none':
      return 'none'
  }
}
